import { without } from "../fields";
import { hexDistance, hexEquals } from "../hex";
import { workingCities } from "../land/cities";
import { isHeldUnit, seatOfUnit, seatUnit, soldiersOwnUnit, withSeat } from "../run/seats";
import type { CoinRules, RunRules } from "../types/rules";
import type { FactionId, Role, RunState, SeatState, Unit } from "../types/state";
import { isBroken } from "../units/morale";
import { isAtWork } from "./company";
import { purseOf } from "./prospect";
import { seatTitle } from "./valor";

/**
 * Command (VC of the Soldier's growth, docs/design/SOLDIER-GROWTH-IDEA.md section 4.1): coins
 * make a seat's soldier a leader for this life, and idle soldiers of its kingdom beside it join
 * it by contact, as every commander's do. The lead is the unit's own field (`leadsSince`), never a
 * commander's badge, so it dies with the unit and no script reads it.
 */

/** The coins block of the run's rules; undefined while it is off. */
export function coinRulesOf(rules: RunRules): CoinRules | undefined {
  return rules.soldier?.coins;
}

/** The seat's soldier while it leads; undefined otherwise. */
export function seatLeader(state: RunState, seat: SeatState | undefined): Unit | undefined {
  const unit = seatUnit(state, seat);
  return unit?.leadsSince === undefined ? undefined : unit;
}

/** A unit that leads a squad: a commander, or a seat's soldier while it leads (VC). */
export function leadsSquad(unit: Unit): boolean {
  return unit.commander || unit.leadsSince !== undefined;
}

/** How many men the seat's soldier may lead now: its title's (`men`); 0 without the block. */
export function leaderRoom(state: RunState, seat: SeatState | undefined): number {
  return coinRulesOf(state.rules)?.men[seatTitle(state, seat)] ?? 0;
}

/**
 * Whether `unit` falls in with `leader` by contact (the rule `gather` applies): another unit of
 * its kingdom beside it, no leader itself, in no squad, unbroken, idle or sent to the leader's hex,
 * and never a held seat's (D8).
 */
export function joinsByContact(state: RunState, unit: Unit, leader: Unit): boolean {
  return (
    !isHeldUnit(state, unit.id) &&
    unit.id !== leader.id &&
    unit.owner === leader.owner &&
    !unit.commander &&
    unit.squadOf === undefined &&
    unit.brokenTick === undefined &&
    (unit.destination === undefined || hexEquals(unit.destination, leader.coord)) &&
    hexDistance(unit.coord, leader.coord) === 1
  );
}

/**
 * Why the seat's soldier may not buy Command now, in the order the screen names it: the block
 * is off; no soldier stands (or the run is over); it leads already; it was not sent to work this
 * life; it is broken; it is below its first man's title; it is not in or beside an own working
 * city; its hands are full; it lacks the coins; nobody beside it would join. Undefined when it may.
 */
export type CommandBlocker =
  | "off"
  | "seat"
  | "leads"
  | "work"
  | "broken"
  | "title"
  | "city"
  | "hands"
  | "coins"
  | "men";

export function commandOffer(
  state: RunState,
  seat: SeatState | undefined,
): CommandBlocker | undefined {
  const coins = coinRulesOf(state.rules);
  // Men join by the court's own rule (`gather`); without a court nobody would.
  if (!coins || !state.rules.soldier?.valor || !state.rules.court) {
    return "off";
  }
  const unit = seatUnit(state, seat);
  if (!unit || state.status !== "running") {
    return "seat";
  }
  if (unit.leadsSince !== undefined) {
    return "leads";
  }
  if (!isAtWork(state, unit)) {
    return "work";
  }
  if (isBroken(state, unit)) {
    return "broken";
  }
  if (leaderRoom(state, seat) <= 0) {
    return "title";
  }
  if (!workingCities(state, unit.owner).some((city) => hexDistance(city.coord, unit.coord) <= 1)) {
    return "city";
  }
  if (unit.carry) {
    return "hands";
  }
  if (purseOf(seat) < coins.commandPrice) {
    return "coins";
  }
  return state.units.some((other) => joinsByContact(state, other, unit)) ? undefined : "men";
}

/**
 * A seat's soldier buys Command (VC), by its own word (D8): the coins vanish into no pool and
 * are counted spent, the lead replaces an enlistment, and the men beside it fall in at once, by
 * `gather`'s own test and up to its title's room (the offer named them, so a walk away the same
 * instant cannot lose them); later ones fall in at the move steps. Same object back when
 * `commandOffer` names a blocker.
 */
export function takeCommand(
  state: RunState,
  faction: FactionId,
  unitId: number,
  issuer: Role,
): RunState {
  const unit = soldiersOwnUnit(state, faction, unitId, issuer);
  const coins = coinRulesOf(state.rules);
  const seat = seatOfUnit(state, unitId);
  if (!unit || !coins || !seat || commandOffer(state, seat) !== undefined) {
    return state;
  }
  const leader: Unit = { ...unit, leadsSince: state.tick };
  let room = leaderRoom(state, seat);
  const led: RunState = {
    ...state,
    units: state.units.map((other) => {
      if (other.id === unit.id) {
        return leader;
      }
      if (room > 0 && joinsByContact(state, other, leader)) {
        room -= 1;
        return { ...without(other, "destination"), squadOf: unit.id };
      }
      return other;
    }),
  };
  return withSeat(led, {
    ...without(seat, "enlisted"),
    purse: purseOf(seat) - coins.commandPrice,
    spent: (seat.spent ?? 0) + coins.commandPrice,
  });
}

/** A seat's soldier stands down (VC): the lead ends, its men are free, the coins stay spent. */
export function standDown(
  state: RunState,
  faction: FactionId,
  unitId: number,
  issuer: Role,
): RunState {
  const unit = soldiersOwnUnit(state, faction, unitId, issuer);
  if (!unit || unit.leadsSince === undefined) {
    return state;
  }
  return {
    ...state,
    units: state.units.map((other) => {
      if (other.id === unit.id) {
        return without(other, "leadsSince");
      }
      return other.squadOf === unit.id ? without(other, "squadOf") : other;
    }),
  };
}
